import { useState, useEffect } from 'react';
import {
  Button,
  Card,
  Text,
  Select,
  Checkbox,
  Switch,
  Textarea,
  makeStyles,
  tokens,
  Spinner,
} from '@fluentui/react-components';
import { ArrowDownload24Regular } from '@fluentui/react-icons';
import JSZip from 'jszip';
import { useApiClient, customerService, TenantRecord } from '../../services/apiService';
import { SPACING, TYPOGRAPHY } from '../../styles/constants';

const useStyles = makeStyles({
  page: { maxWidth: '700px', padding: SPACING.lg },
  title: { ...TYPOGRAPHY.h2, marginBottom: SPACING.xl },
  card: { padding: SPACING.xl, marginBottom: SPACING.lg },
  sectionTitle: { ...TYPOGRAPHY.h3, marginBottom: SPACING.xs },
  sectionDesc: { ...TYPOGRAPHY.bodySmall, color: tokens.colorNeutralForeground3, marginBottom: SPACING.lg },
  form: { display: 'flex', flexDirection: 'column', gap: SPACING.lg },
  field: { display: 'flex', flexDirection: 'column', gap: SPACING.sm },
  label: { ...TYPOGRAPHY.label, fontWeight: 500 },
  hint: { ...TYPOGRAPHY.bodySmall, color: tokens.colorNeutralForeground3 },
  row: { display: 'flex', gap: SPACING.lg, '@media (max-width: 640px)': { flexDirection: 'column' } },
  checkboxGroup: { display: 'flex', flexDirection: 'column', gap: SPACING.md },
  footer: { display: 'flex', justifyContent: 'flex-end', marginTop: SPACING.md },
  success: { color: tokens.colorStatusSuccessForeground1, ...TYPOGRAPHY.bodySmall },
  error: { color: tokens.colorStatusDangerForeground1, ...TYPOGRAPHY.bodySmall },
});

const INDUSTRIES = [
  'Financial Services', 'Healthcare', 'Legal & Professional Services',
  'Technology', 'Retail & Consumer', 'Manufacturing', 'Education',
  'Government & Public Sector', 'Media & Communications', 'Other',
];

const ORG_SIZES = ['1–50', '51–200', '201–1,000', '1,001–5,000', '5,000+'];
const COUNTRIES = [
  'United Kingdom', 'United States', 'Australia', 'New Zealand', 'Canada',
  'Germany', 'France', 'Netherlands', 'Sweden', 'Singapore', 'Other',
];

export default function CustomerSettings() {
  const styles = useStyles();
  useApiClient();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    org_country: '',
    org_industry: '',
    org_size_bracket: '',
    org_context: '',
    supports_m365_copilot: false,
    supports_copilot_studio: false,
    supports_azure_ai: false,
    org_notes: '',
    is_access_restricted: false,
  });

  useEffect(() => {
    customerService.getMe()
      .then((t: TenantRecord) => {
        const policy = t.it_policy || {};
        setForm({
          org_country: t.org_country || '',
          org_industry: t.org_industry || '',
          org_size_bracket: t.org_size_bracket || '',
          org_context: t.org_context || '',
          supports_m365_copilot: policy.supports_m365_copilot ?? false,
          supports_copilot_studio: policy.supports_copilot_studio ?? false,
          supports_azure_ai: policy.supports_azure_ai ?? false,
          org_notes: policy.org_notes || '',
          is_access_restricted: t.is_access_restricted ?? false,
        });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const set = (field: string, value: any) => setForm((f) => ({ ...f, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      await customerService.updateSettings({
        org_country: form.org_country || undefined,
        org_industry: form.org_industry || undefined,
        org_size_bracket: form.org_size_bracket || undefined,
        org_context: form.org_context || undefined,
        is_access_restricted: form.is_access_restricted,
        it_policy: {
          supports_m365_copilot: form.supports_m365_copilot,
          supports_copilot_studio: form.supports_copilot_studio,
          supports_azure_ai: form.supports_azure_ai,
          org_notes: form.org_notes || null,
        },
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadManifest = async () => {
    const botAppId = import.meta.env.VITE_BOT_APP_ID;
    const appEnv = (import.meta.env.VITE_APP_ENV || 'dev').toLowerCase();
    const envSuffix = appEnv === 'staging' ? ' Staging' : (appEnv === 'prod' || appEnv === 'production' ? '' : ' Dev');
    const envFileSuffix = appEnv === 'staging' ? '-staging' : (appEnv === 'prod' || appEnv === 'production' ? '' : '-dev');
    if (!botAppId) { setError('Teams manifest not configured. Contact your Sherpai account manager.'); return; }

    setDownloading(true);
    try {
      const [manifestRes, colorRes, outlineRes] = await Promise.all([
        fetch('/teams/manifest.json'),
        fetch('/teams/color.png'),
        fetch('/teams/outline.png'),
      ]);

      const manifestText = await manifestRes.text();
        let manifest = manifestText.replace(/\{\{BOT_APP_ID\}\}/g, botAppId);
        manifest = manifest.replace(/\{\{ENV_SUFFIX\}\}/g, envSuffix);

      const zip = new JSZip();
      zip.file('manifest.json', manifest);
      zip.file('color.png', await colorRes.blob());
      zip.file('outline.png', await outlineRes.blob());

      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sherpai${envFileSuffix}-teams.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download manifest. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <div style={{ padding: '48px', textAlign: 'center' }}><Spinner /></div>;

  return (
    <div className={styles.page}>
      <Text className={styles.title}>Settings</Text>

      {/* Org details */}
      <Card className={styles.card}>
        <Text className={styles.sectionTitle}>Organisation details</Text>
        <Text className={styles.sectionDesc}>
          Used by Sherpai to apply the right regulatory context and industry knowledge.
        </Text>
        <div className={styles.form}>
          <div className={styles.row}>
            <div className={styles.field} style={{ flex: 1 }}>
              <Text className={styles.label}>Country</Text>
              <Select value={form.org_country} onChange={(_, d) => set('org_country', d.value)}>
                <option value="">Select country</option>
                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </Select>
            </div>
            <div className={styles.field} style={{ flex: 1 }}>
              <Text className={styles.label}>Organisation size</Text>
              <Select value={form.org_size_bracket} onChange={(_, d) => set('org_size_bracket', d.value)}>
                <option value="">Select size</option>
                {ORG_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
              </Select>
            </div>
          </div>
          <div className={styles.field}>
            <Text className={styles.label}>Industry</Text>
            <Select value={form.org_industry} onChange={(_, d) => set('org_industry', d.value)}>
              <option value="">Select industry</option>
              {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
            </Select>
          </div>
        </div>
      </Card>

      {/* IT policy */}
      <Card className={styles.card}>
        <Text className={styles.sectionTitle}>Microsoft AI tools</Text>
        <Text className={styles.sectionDesc}>
          Which Microsoft AI tools does your organisation support? Sherpai uses this to ensure
          users only get recommendations for tools they can actually access.
        </Text>
        <div className={styles.checkboxGroup}>
          <Checkbox
            label="Microsoft 365 Copilot (paid — in Word, Excel, Teams, Outlook)"
            checked={form.supports_m365_copilot}
            onChange={(_, d) => set('supports_m365_copilot', d.checked)}
          />
          <Checkbox
            label="Copilot Studio (custom agent / chatbot builder)"
            checked={form.supports_copilot_studio}
            onChange={(_, d) => set('supports_copilot_studio', d.checked)}
          />
          <Checkbox
            label="Azure AI / Azure OpenAI (custom application development)"
            checked={form.supports_azure_ai}
            onChange={(_, d) => set('supports_azure_ai', d.checked)}
          />
        </div>
        <div className={styles.field} style={{ marginTop: '16px' }}>
          <Text className={styles.label}>IT notes</Text>
          <Textarea
            value={form.org_notes}
            onChange={(_, d) => set('org_notes', d.value)}
            placeholder="e.g. All AI use must be approved by IT Security. No client data in AI tools."
            rows={3}
          />
          <Text className={styles.hint}>Any policies or restrictions shown to users when they ask</Text>
        </div>
      </Card>

      {/* AI strategy */}
      <Card className={styles.card}>
        <Text className={styles.sectionTitle}>AI strategy and context</Text>
        <Text className={styles.sectionDesc}>
          Your organisation's view on AI adoption. Sherpai uses this to frame every answer in the
          context of your strategy rather than giving generic guidance.
        </Text>
        <div className={styles.field}>
          <Textarea
            value={form.org_context}
            onChange={(_, d) => set('org_context', d.value)}
            placeholder="e.g. We are prioritising AI use cases that save time on reporting and client communications. Staff should not input PII into AI tools without IT approval..."
            rows={6}
          />
          <Text className={styles.hint}>Stored securely · never shared externally · used only to shape answers for your team</Text>
        </div>
      </Card>

      {/* Save */}
      <div className={styles.footer} style={{ gap: '12px', alignItems: 'center' }}>
        {saved && <Text className={styles.success}>Saved</Text>}
        {error && <Text className={styles.error}>{error}</Text>}
        <Button appearance="primary" onClick={handleSave} disabled={saving}>
          {saving ? <Spinner size="tiny" /> : 'Save changes'}
        </Button>
      </div>

      {/* Access control */}
      <Card className={styles.card} style={{ marginTop: '20px' }}>
        <Text className={styles.sectionTitle}>Access control</Text>
        <Text className={styles.sectionDesc}>
          When enabled, only users you have added in Team Members can use the Sherpai bot.
          Anyone not on the list will be told to contact their IT admin.
        </Text>
        <Switch
          label={form.is_access_restricted ? 'Restricted — invited users only' : 'Open — anyone in your organisation'}
          checked={form.is_access_restricted}
          onChange={(_, d) => set('is_access_restricted', d.checked)}
        />
      </Card>

      {/* Teams manifest */}
      <Card className={styles.card} style={{ marginTop: '20px' }}>
        <Text className={styles.sectionTitle}>Teams deployment</Text>
        <Text className={styles.sectionDesc}>
          Download the Teams app package to sideload Sherpai for your organisation, or upload it
          to the Teams Admin Centre for organisation-wide deployment.
        </Text>
        <Button
          icon={<ArrowDownload24Regular />}
          onClick={handleDownloadManifest}
          disabled={downloading}
        >
          {downloading ? <Spinner size="tiny" /> : 'Download Teams manifest (.zip)'}
        </Button>
      </Card>
    </div>
  );
}
